<template>
  <div
    v-if="cart?.items?.length && promoCodeEnabled"
    class="cart-coupon"
    :class="{ 'cart-coupon--order-page': isOrderPage }"
  >
    <button
      v-if="!showForm"
      type="button"
      class="cart-coupon__trigger"
      @click="openForm"
    >
      {{ triggerLabelText }}
    </button>
    <form
      v-else
      method="post"
      action=""
      class="cart-coupon__form"
      @submit.prevent="applyCouponAction"
    >
      <div class="cart-coupon__field">
        <input
          v-model="coupon"
          type="text"
          :placeholder="$t('labels.placeholderCoupon')"
          name="coupon"
          class="cart-coupon__input"
          autocomplete="off"
          spellcheck="false"
        />
        <button type="submit" name="applycoupon" class="cart-coupon__apply">
          {{ $t("labels.applycoupon") }}
        </button>
      </div>
    </form>
    <div
      v-for="(message, index) in messages"
      :key="`${message.type}-${index}`"
      :class="[
        'cart-coupon__feedback',
        `cart-coupon__feedback--${message.type}`,
      ]"
      role="alert"
    >
      {{ message.message }}
    </div>
  </div>
</template>
<script>
import { useCartStore } from "@/store/cart.js";
import CouponService from "@/services/coupon";
import {
  getOrderPageContextVersion,
  isOrderPageContext,
} from "@/services/orderPageScope";

const DEFAULT_LINK_LABEL_KEY = "labels.addDiscountCodeLink";

export default {
  name: "CartCoupon",
  inheritAttrs: false,
  props: {
    linkLabel: { type: String, default: "" },
    label: { type: String, default: "" },
    formCssClass: { type: String, default: "" },
    inputCssClass: { type: String, default: "" },
    btnCssClass: { type: String, default: "" },
  },
  data() {
    return {
      cart: null,
      coupon: "",
      messages: [],
      messageTimeout: null,
      showForm: false,
      domLinkLabel: "",
    };
  },
  computed: {
    lastCartUpdate() {
      return useCartStore().lastUpdate;
    },
    promoCodeEnabled() {
      return window?.globalConfig?.promoCodeEnabled !== false;
    },
    isOrderPage() {
      getOrderPageContextVersion().value;
      return isOrderPageContext();
    },
    triggerLabelText() {
      const candidates = [
        this.linkLabel,
        this.label,
        this.domLinkLabel,
        this.$attrs["link-label"],
        this.$attrs["data-link-label"],
        this.$attrs.label,
      ];
      for (const value of candidates) {
        const text = (value || "").trim();
        if (text) {
          return text;
        }
      }
      if (this.$te(DEFAULT_LINK_LABEL_KEY)) {
        return this.$t(DEFAULT_LINK_LABEL_KEY);
      }
      return "";
    },
  },
  watch: {
    lastCartUpdate: {
      handler() {
        if (!window?.globalConfig?.domainId) {
          return;
        }
        this.cart = this.getCart();
      },
    },
    messages: {
      handler() {
        if (this.messages.length === 0) {
          return;
        }
        if (this.messageTimeout) {
          window.clearTimeout(this.messageTimeout);
        }
        this.messageTimeout = window.setTimeout(() => {
          this.messages = [];
        }, 5000);
      },
      deep: true,
    },
  },
  mounted() {
    this.cart = this.getCart();
    const el = this.$el;
    if (el && typeof el.getAttribute === "function") {
      this.domLinkLabel =
        el.getAttribute("link-label") ||
        el.getAttribute("data-link-label") ||
        el.getAttribute("label") ||
        "";
    }
    // Templates legacy : ouvrir si le HTML avait déjà retiré d-none via page.js
    if (
      !this.isOrderPage &&
      this.$el?.querySelector?.(".cart-coupon__form:not(.d-none)")
    ) {
      this.showForm = true;
    }
  },
  methods: {
    getCart() {
      return useCartStore().cart;
    },
    openForm() {
      this.showForm = true;
      this.$nextTick(() => {
        this.$el.querySelector(".cart-coupon__input")?.focus();
      });
    },
    applyCouponAction() {
      this.messages = [];
      if (this.coupon === "") {
        this.messages.push({
          type: "danger",
          message: this.$t("messages.emptyCoupon"),
        });
        return;
      }

      this.coupon = this.coupon.toUpperCase().trim();
      const minifiedCart = CouponService.addCouponInMinifiedCart(this.coupon);
      CouponService.checkCoupons(minifiedCart)
        .then((response) => {
          for (const coupon of Object.values(response.data.cart.coupons)) {
            if (coupon.code === this.coupon) {
              document.dispatchEvent(
                new CustomEvent("cart-updated-campaign-stats", {
                  detail: { eventType: "coupon_add", eventDetails: coupon },
                }),
              );
            }
            if (coupon.isValid === false) {
              let translation = "";
              if (this.$te(`messages.invalidCoupon.${coupon.reason}`)) {
                translation = this.$t(
                  `messages.invalidCoupon.${coupon.reason}`,
                  {
                    invalidCouponCode: coupon.code,
                    invalidCouponReason: coupon.reason,
                  },
                );
              } else {
                translation = this.$t("messages.invalidCoupon.DEFAULT", {
                  invalidCouponCode: coupon.code,
                  invalidCouponReason: coupon.reason,
                });
              }
              this.messages.push({
                type: "danger",
                message: translation,
              });
            } else {
              this.messages.push({
                type: "success",
                message: this.$t("messages.successfulCoupon", {
                  validCouponCode: coupon.code,
                }),
              });
              this.coupon = "";
            }
          }
        })
        .catch(() => {
          this.messages.push({
            type: "danger",
            message: this.$t("messages.errorCoupon"),
          });
        });
    },
  },
};
</script>
